
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { HolidayPackage, Hotel, Tour, SearchQuery, ApiLogEntry, FilterCriteria, TravelMateResponse, SearchResultCTA } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { ResultsHeader } from './ResultsHeader';
import { FilterSidebar, normalizeFilterText } from './FilterSidebar';
import { HotelCard } from './HotelCard';
import { TravelMateAgent } from './TravelMateAgent';
import { getImagesForDestination } from '../services/imageService';
import { searchWithCache, searchTourvisio, getPagingData, SearchResponse, getAlternativeDatesFromCache, PriceBandDate } from '../services/tourvisioService';
import { PriceBand } from './PriceBand';
import { TravelAssistant } from './TravelAssistant';
import { saveHotelToCMS, getHotelFromCMS, getSearchCTAs, findHotelContent, getSearchResultsAds } from '../services/dataService';
import PageCTA from './PageCTA';
import { streamSearchCommentary, getQuickHotelSummary } from '../services/aiService';
import { getTravelMateAdvice } from '../services/aiService';
import { SearchType } from '../types';

interface SearchResultsProps {
    logApiCall: (log: ApiLogEntry) => void;
}

const InFeedCTA: React.FC<{ cta: SearchResultCTA; language: 'en' | 'sv' }> = ({ cta, language }) => (
    <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-lg my-6 flex flex-col md:flex-row transform hover:-translate-y-1 transition-all duration-300">
        <div className="md:w-1/3 h-48 md:h-auto relative">
            <img src={cta.imageUrl} alt={cta.title[language]} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
        </div>
        <div className="p-6 md:w-2/3 flex flex-col justify-center items-start text-white">
            <span className="text-amber-500 font-bold text-xs uppercase tracking-wider mb-2">{language === 'sv' ? 'Utvalt Erbjudande' : language === 'da' ? 'Udvalgt Tilbud' : 'Featured Offer'}</span>
            <h3 className="text-2xl font-bold mb-2">{cta.title[language]}</h3>
            <p className="text-gray-300 mb-4">{cta.description[language]}</p>
            <button className="bg-white text-gray-900 px-6 py-2 rounded-full font-bold text-sm hover:bg-amber-500 hover:text-white transition-colors">
                {cta.buttonText[language]}
            </button>
        </div>
    </div>
);

const LoadingProgress: React.FC<{ commentary: string; query: SearchQuery | null; step: number }> = ({ commentary, query, step }) => {
    const { t, language } = useLocalization();

    const steps = [
        language === 'sv' ? 'Initierar AI-sökning...' : language === 'da' ? 'Initialiserer AI-søgning...' : 'Initializing AI Search...',
        language === 'sv' ? 'Ansluter till Tourvisio API...' : language === 'da' ? 'Forbinder til Tourvisio API...' : 'Connecting to Tourvisio API...',
        language === 'sv' ? 'Analyserar lokala marknadstrender...' : language === 'da' ? 'Analyserer lokale markedstendenser...' : 'Analyzing local market trends...',
        language === 'sv' ? 'Finnar de bästa priserna...' : language === 'da' ? 'Finder de bedste priser...' : 'Finding best available rates...',
        language === 'sv' ? 'Genererar personliga insikter...' : language === 'da' ? 'Genererer personlige indsigter...' : 'Generating personalized insights...'
    ];

    return (
        <div className="flex flex-col items-center justify-center p-8 bg-white/50 backdrop-blur-md rounded-3xl border border-white shadow-inner mb-8 animate-fadeIn">
            <div className="relative mb-8">
                <div className="absolute inset-0 bg-amber-500/20 blur-3xl rounded-full scale-150 animate-pulse"></div>
                <div className="relative z-10 w-24 h-24 flex items-center justify-center">
                    <svg className="animate-spin h-16 w-16 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center text-xs font-black text-amber-800">
                        {Math.min(100, (step + 1) * 20)}%
                    </div>
                </div>
            </div>

            <h2 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tighter">
                {t('searching_for_deals')}
            </h2>

            <div className="flex gap-1 mb-6">
                {[0, 1, 2, 3, 4].map(s => (
                    <div key={s} className={`h-1 w-8 rounded-full transition-all duration-500 ${s <= step ? 'bg-amber-500 w-12' : 'bg-gray-200'}`} />
                ))}
            </div>

            {commentary ? (
                <div className="max-w-xl text-center bg-white/80 p-6 rounded-2xl shadow-sm border border-amber-100">
                    <div className="inline-block px-3 py-1 bg-amber-500 text-white text-[10px] font-black rounded-full mb-3 uppercase tracking-widest shadow-lg shadow-amber-500/30">
                        {t('ai_insights')}
                    </div>
                    <p className="text-xl font-bold text-gray-800 italic leading-relaxed">
                        "{commentary}"
                    </p>
                </div>
            ) : (
                <div className="space-y-2 w-64">
                    <p className="text-sm text-amber-700 text-center font-bold animate-pulse">
                        {steps[step] || steps[steps.length - 1]}
                    </p>
                </div>
            )}
        </div>
    );
};

// Helper to strip heavy data for caching to avoid QuotaExceededError
const compressForCache = (results: (HolidayPackage | Hotel | Tour)[]) => {
    // Keep only top 40 results and essential fields for the list view
    return results.slice(0, 40).map(item => {
        // We need basic fields for display and ID for refetching details
        if ('pricePerNight' in item) {
            const h = item as Hotel;
            return {
                id: h.id,
                productId: h.productId,
                provider: h.provider,
                name: h.name,
                location: h.location,
                rating: h.rating,
                pricePerNight: h.pricePerNight,
                currency: h.currency,
                imageUrl: h.imageUrl,
                thumbnailUrl: h.thumbnailUrl,
                board: h.board,
                roomType: h.roomType,
                themes: h.themes || [],
                facilities: [], // Strip facilities to save space
                offers: [], // Strip heavy offers array
                // Keep description if it's short or just boolean indicator
                isEdited: h.isEdited
            };
        }
        return item;
    });
};

// PREFERENCE_TAGS moved to FilterSidebar as AI_PREFERENCE_TAGS

export const SearchResults: React.FC<SearchResultsProps> = ({ logApiCall }) => {
    const { t, language } = useLocalization();
    const location = useLocation();
    const navigate = useNavigate();

    // State for query and results
    const [searchQuery, setSearchQuery] = useState<SearchQuery | null>(null);
    const [results, setResults] = useState<(HolidayPackage | Hotel | Tour)[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [alternativeDates, setAlternativeDates] = useState<PriceBandDate[]>([]);
    const [isPriceBandLoading, setIsPriceBandLoading] = useState(false);

    const [travelMateAdvice, setTravelMateAdvice] = useState<TravelMateResponse | null>(null);
    const [isLoadingAdvice, setIsLoadingAdvice] = useState(false);
    const [isAssistantOpen, setIsAssistantOpen] = useState(false);
    const [aiEnabled, setAIEnabled] = useState(false); // TravelMate AI default OFF

    const [filters, setFilters] = useState<FilterCriteria>({});
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [destinationImages, setDestinationImages] = useState<string[]>([]);
    const [activeCTAs, setActiveCTAs] = useState<any[]>([]);
    const [ads, setAds] = useState<any[]>([]);
    const [searchCommentary, setSearchCommentary] = useState<string>('');
    const [quickSummaries, setQuickSummaries] = useState<Record<string, string>>({});
    const [loadingStep, setLoadingStep] = useState(0);

    // Tailor Trip State
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [hasUpdatedAdvice, setHasUpdatedAdvice] = useState(false);

    const [displayLimit, setDisplayLimit] = useState(50);
    const loadingMoreRef = useRef<HTMLDivElement>(null);

    // Sync CTAs with Admin updates
    useEffect(() => {
        const updateCTAs = async () => setActiveCTAs(await getSearchCTAs());
        updateCTAs(); // Initial load

        window.addEventListener('tourvisio_data_update', updateCTAs);
        window.addEventListener('storage', updateCTAs);
        return () => {
            window.removeEventListener('tourvisio_data_update', updateCTAs);
            window.removeEventListener('storage', updateCTAs);
        }
    }, []);

    // Initialization: Logic to use passed state OR cache (30-min TTL)
    useEffect(() => {
        const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

        if (location.state?.searchQuery) {
            const incomingQuery = location.state.searchQuery;

            // Check if cache matches this exact query and is fresh (< 30 min)
            const cachedQuery = sessionStorage.getItem('last_search_query');
            const cachedResults = sessionStorage.getItem('last_search_results');
            const cachedTimestamp = sessionStorage.getItem('last_search_timestamp');

            const isCacheFresh = cachedTimestamp && (Date.now() - Number(cachedTimestamp)) < CACHE_TTL_MS;
            const isSameQuery = cachedQuery && cachedQuery === JSON.stringify(incomingQuery);

            if (isSameQuery && isCacheFresh && cachedResults) {
                // Back-navigation with fresh cache — restore instantly, no API call
                try {
                    setSearchQuery(incomingQuery);
                    setResults(JSON.parse(cachedResults));
                    searchTourvisio(incomingQuery as any, language as any, (log) => console.log('[Tour Assist AI] Tourvisio log:', log));
                    setIsLoading(false);
                    console.log('[SearchResults] Restored from cache (age: ' + Math.round((Date.now() - Number(cachedTimestamp)) / 1000) + 's)');
                    return;
                } catch (e) {
                    console.error('Failed to parse cached results', e);
                }
            }

            // New search or stale cache — clear and re-search
            setSearchQuery(incomingQuery);
            sessionStorage.removeItem('last_search_results');
            sessionStorage.removeItem('last_search_query');
            sessionStorage.removeItem('last_search_timestamp');
        } else {
            // No state — check sessionStorage cache
            const cachedQuery = sessionStorage.getItem('last_search_query');
            const cachedResults = sessionStorage.getItem('last_search_results');
            const cachedTimestamp = sessionStorage.getItem('last_search_timestamp');

            const isCacheFresh = cachedTimestamp && (Date.now() - Number(cachedTimestamp)) < CACHE_TTL_MS;

            if (cachedQuery && cachedResults && isCacheFresh) {
                try {
                    setSearchQuery(JSON.parse(cachedQuery));
                    setResults(JSON.parse(cachedResults));
                } catch (e) {
                    console.error('Failed to parse cached results', e);
                }
                setIsLoading(false);
            } else {
                // No state, no fresh cache -> Redirect home
                navigate('/');
            }
        }
    }, [location.state, navigate, language]);

    // Calculate Global Price Stats for Budget Detection
    const globalPrices = useMemo(() => {
        return results.filter(r => 'pricePerNight' in r).map(r => (r as Hotel).pricePerNight).filter(p => p > 0);
    }, [results]);

    const globalMaxPrice = useMemo(() => {
        return globalPrices.length > 0 ? Math.ceil(Math.max(...globalPrices)) : 10000;
    }, [globalPrices]);

    // State for SearchId and Pagination
    const [searchId, setSearchId] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);


    // ... (keep existing effects)

    // Perform Search Logic
    useEffect(() => {
        if (searchQuery) {
            setIsPriceBandLoading(true);
            getAlternativeDatesFromCache(searchQuery)
                .then(dates => {
                    setAlternativeDates(dates);
                    setIsPriceBandLoading(false);
                })
                .catch(() => setIsPriceBandLoading(false));
        }

        // Only search if we have a query AND no results yet (implies fresh search)
        // If results exist (from cache), skip this.
        if (!searchQuery || results.length > 0) {
            // If restored from cache, ensure we use the cached searchId if available
            const cachedSearchId = sessionStorage.getItem('last_search_id');
            if (cachedSearchId && !searchId) setSearchId(cachedSearchId);
            return;
        }

        const performSearch = async () => {
            if (!searchQuery) return;
            setIsLoading(true);
            setSearchId('');
            setCurrentPage(1);
            setSearchCommentary('');
            setQuickSummaries({});
            setLoadingStep(1);

            // Step progression simulation
            const steps = [1, 2, 3, 4];
            steps.forEach((step, i) => {
                setTimeout(() => setLoadingStep(step), (i + 1) * 2000);
            });

            // Trigger Real-time AI Commentary
            streamSearchCommentary(searchQuery, language, (text) => {
                setSearchCommentary(text);
                setLoadingStep(5); // AI jumped ahead or done
            });

            // Clear previous results immediately to avoid confusion
            if (results.length === 0) {
                // Keep showing skeleton?
            }

            try {
                // Use direct searchTourvisio to bypass backend cache
                const responseResults = await searchTourvisio(searchQuery, language, logApiCall);

                const enrichedData = await Promise.all(responseResults.map(async item => {
                    if ('pricePerNight' in item) {
                        const hotelItem = item as Hotel;
                        // Try finding by ID first, then fuzzy name match for content
                        const savedHotel = await findHotelContent(hotelItem.id, hotelItem.name);

                        if (savedHotel && savedHotel.isEdited) {
                            const mergedHotel = { ...hotelItem };
                            if (savedHotel.description) mergedHotel.description = savedHotel.description;
                            if (savedHotel.shortDescription) mergedHotel.shortDescription = savedHotel.shortDescription;

                            // Fix for Image Logic:
                            // We only use the cached image if it is NOT a broken local path (starting with /media/)
                            // OR if we don't have a fresh API image to show.
                            if (savedHotel.images && savedHotel.images.length > 0) {
                                const cachedImg = savedHotel.images[0];
                                const cachedImgUrl = typeof cachedImg === 'string' ? cachedImg : cachedImg.url;
                                const hasFreshApiImage = hotelItem.imageUrl && (hotelItem.imageUrl.startsWith('http') || hotelItem.imageUrl.startsWith('//'));

                                const isBrokenLocalPath = cachedImgUrl.startsWith('/media/');

                                if (!isBrokenLocalPath || !hasFreshApiImage) {
                                    mergedHotel.images = savedHotel.images;
                                    mergedHotel.imageUrl = cachedImgUrl;
                                    mergedHotel.thumbnailUrl = cachedImgUrl;
                                }
                            }
                            mergedHotel.isEdited = true;
                            return mergedHotel;
                        }
                    }
                    return item;
                }));

                setResults(enrichedData);

                // Cache Results - Robust handling for quota exceeded
                try {
                    sessionStorage.removeItem('last_search_results');
                    sessionStorage.removeItem('last_search_query');

                    sessionStorage.setItem('last_search_results', JSON.stringify(enrichedData));
                    sessionStorage.setItem('last_search_query', JSON.stringify(searchQuery));
                    sessionStorage.setItem('last_search_timestamp', String(Date.now()));
                } catch (e) {
                    // Try caching compressed if full fails
                    try {
                        const truncatedData = compressForCache(enrichedData);
                        sessionStorage.setItem('last_search_results', JSON.stringify(truncatedData));
                        sessionStorage.setItem('last_search_query', JSON.stringify(searchQuery));
                        sessionStorage.setItem('last_search_timestamp', String(Date.now()));
                    } catch (e2) { }
                }

                // Cache any new results to CMS via API (fire-and-forget)
                enrichedData.forEach(item => {
                    if ('pricePerNight' in item) saveHotelToCMS(item as Hotel); // async, no await needed
                });

                // AI advice is now triggered manually via sidebar filter
                // (No auto-fetch on search)

            } catch (error) {
                console.error("Search failed", error);
            } finally {
                setIsLoading(false);
            }
        };

        performSearch();

        const fetchAds = async () => {
            if (searchQuery) {
                const searchAds = await getSearchResultsAds(searchQuery.destinationId);
                setAds(searchAds);
            }
        };
        fetchAds();
    }, [searchQuery, language, logApiCall, results.length]);


    useEffect(() => {
        const fetchImages = async () => {
            if (searchQuery?.destinationName) {
                // Use a more generic query to ensure results, or specific to hotel vibe
                // We fetch 20 images to distribute among results
                const query = `${searchQuery.destinationName} landscape hotel resort`;
                const images = await getImagesForDestination(query, 20);
                setDestinationImages(images);
            }
        };
        fetchImages();
    }, [searchQuery?.destinationName]);

    const hotelResults = useMemo(() => {
        return results.filter(r => 'pricePerNight' in r) as Hotel[];
    }, [results]);

    const filteredResults = useMemo(() => {
        return hotelResults.filter(hotel => {
            if (filters.name && !hotel.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
            if (filters.priceRange) {
                const [min, max] = filters.priceRange;
                if (hotel.pricePerNight < min || hotel.pricePerNight > max) return false;
            }
            if (filters.stars && filters.stars.length > 0 && !filters.stars.includes(Math.floor(hotel.rating))) return false;
            if (filters.boards && filters.boards.length > 0 && !filters.boards.includes(normalizeFilterText(hotel.board))) return false;
            if (filters.themes && filters.themes.length > 0) {
                const hotelThemes = hotel.themes.map(th => normalizeFilterText(th));
                if (!filters.themes.some(t => hotelThemes.includes(t))) return false;
            }
            if (filters.facilities && filters.facilities.length > 0) {
                const hotelFacilities = hotel.facilities.map(f => normalizeFilterText(f));
                if (!filters.facilities.some(f => hotelFacilities.includes(f))) return false;
            }
            return true;
        });
    }, [hotelResults, filters]);

    // Merge Results with CTAs
    const mixedResults = useMemo(() => {
        // Filter Active and Scheduled CTAs from the state
        const now = new Date();
        const currentCtas = activeCTAs.filter(c => {
            if (!c.isActive) return false;
            if (c.startDate && new Date(c.startDate) > now) return false;
            if (c.endDate) {
                const end = new Date(c.endDate);
                end.setHours(23, 59, 59, 999);
                if (end < now) return false;
            }
            
            // Validate Entity Targeting Constraints
            if (c.hotelId && c.hotelId !== searchQuery?.hotelId) return false;
            if (c.destinationId && c.destinationId !== searchQuery?.destinationId) return false;

            return true;
        }).sort((a, b) => a.positionIndex - b.positionIndex);

        const items: React.ReactNode[] = [];
        const visibleHotels = filteredResults.slice(0, displayLimit);

        visibleHotels.forEach((hotel, index) => {
            items.push(
                <div key={`${hotel.id}-${index}`} className="relative">
                    <HotelCard
                        hotel={hotel}
                        fallbackImage={destinationImages.length > 0 ? destinationImages[index % destinationImages.length] : undefined}
                        searchQuery={searchQuery || undefined}
                        logApiCall={logApiCall}
                        language={language}
                    />
                    {aiEnabled && quickSummaries[hotel.id] && (
                        <div className="absolute -top-2 -right-2 z-10 animate-fade-in-up">
                            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-2 rounded-xl shadow-xl max-w-[200px] border border-white/20">
                                <div className="flex items-center gap-1.5 mb-1">
                                    <span className="text-xs">✨</span>
                                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80">{t('ai_highlight')}</span>
                                </div>
                                <p className="text-[10px] leading-tight font-medium italic">"{quickSummaries[hotel.id]}"</p>
                            </div>
                        </div>
                    )}
                </div>
            );

            // Check if any CTA should be inserted after this index
            const cta = currentCtas.find(c => c.positionIndex === index + 1);
            if (cta) {
                items.push(<PageCTA key={`cta-${cta._id || cta.id}`} cta={cta} language={language} variant="in-feed" className="my-8" />);
            }
        });
        return items;
    }, [filteredResults, displayLimit, destinationImages, language, activeCTAs, searchQuery]);



    const handleBack = () => {
        if (location.state?.from) {
            navigate(location.state.from, { state: { restoreQuery: searchQuery } });
        } else {
            navigate('/');
        }
    };

    const handleTagToggle = (tag: string) => {
        setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
        setHasUpdatedAdvice(false);
    };

    // Handle AI toggle from sidebar
    const handleAIToggle = (enabled: boolean) => {
        setAIEnabled(enabled);
        if (!enabled) {
            // Clear AI data when turned off
            setTravelMateAdvice(null);
            setQuickSummaries({});
            setSelectedTags([]);
            setHasUpdatedAdvice(false);
        }
    };

    const handleUpdateAdvice = () => {
        if (!searchQuery || hotelResults.length === 0) return;

        setIsLoadingAdvice(true);
        setTravelMateAdvice(null);

        // Check if user has explicitly modified the price filter from the global maximum
        let userBudget: number | undefined = undefined;
        if (filters.priceRange && filters.priceRange[1] < globalMaxPrice * 0.95) {
            // If the max slider is less than 95% of global max, treat it as a budget constraint
            userBudget = filters.priceRange[1];
        }

        getTravelMateAdvice(searchQuery, hotelResults, selectedTags, language, userBudget).then(advice => {
            setTravelMateAdvice(advice);
            setIsLoadingAdvice(false);
            setHasUpdatedAdvice(true);
        });
    };

    // TravelMate advice auto-triggers when AI is enabled and preferences change
    useEffect(() => {
        if (!aiEnabled || selectedTags.length === 0 || !searchQuery || hotelResults.length === 0) return;

        const debounceTimer = setTimeout(() => {
            handleUpdateAdvice();
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [aiEnabled, selectedTags, hotelResults.length]);


    if (!searchQuery) return null;

    return (
        <div className="bg-gray-100 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white p-4 rounded-md shadow mb-6">
                    <ResultsHeader searchQuery={searchQuery} onBack={handleBack} />
                </div>

                {searchQuery.checkIn && (
                    <PriceBand 
                        currentDate={searchQuery.checkIn} 
                        alternatives={alternativeDates} 
                        isLoading={isPriceBandLoading}
                        onDateSelect={(newDate) => {
                            // Reset everything and trigger a new search
                            const newQuery = { ...searchQuery, checkIn: newDate };
                            setSearchQuery(newQuery);
                            setResults([]);
                            setIsLoading(true);
                            // Update URL state silently so reloads don't break
                            window.history.replaceState({ ...window.history.state, usr: { searchQuery: newQuery } }, '');
                        }} 
                    />
                )}

                <div className="flex flex-col lg:flex-row gap-6 mt-6">
                    <aside className="w-full lg:w-1/4 xl:w-1/5">
                        {/* Mobile: Collapsible filter toggle */}
                        <div className="lg:hidden mb-3">
                            <button
                                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                                className="w-full flex items-center justify-between bg-white px-4 py-3 rounded-md shadow text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                                    {t('search_filters')}
                                    {(filters.stars?.length || filters.boards?.length || filters.themes?.length || filters.name) && (
                                        <span className="bg-amber-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                            {(filters.stars?.length || 0) + (filters.boards?.length || 0) + (filters.themes?.length || 0) + (filters.name ? 1 : 0)}
                                        </span>
                                    )}
                                </span>
                                <svg className={`w-4 h-4 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                        </div>
                        {/* Mobile: Show/hide based on toggle. Desktop: Always visible */}
                        <div className={`${mobileFiltersOpen ? 'block' : 'hidden'} lg:block`}>
                            <FilterSidebar
                                results={hotelResults}
                                onFilterChange={setFilters}
                                isAIEnabled={aiEnabled}
                                isAILoading={isLoadingAdvice}
                                selectedPreferences={selectedTags}
                                onAIToggle={handleAIToggle}
                                onPreferenceToggle={handleTagToggle}
                                onAnalyze={handleUpdateAdvice}
                            />
                        </div>
                    </aside>

                    <main className="w-full lg:w-3/4 xl:w-4/5">
                        {/* 1. Dynamic Top Banner */}
                        {ads && ads.length > 0 && (
                            <div className="mb-8">
                                <PageCTA cta={ads[0]} language={language} variant="slim" />
                            </div>
                        )}

                        {/* TRAVELMATE AGENT — only shown when AI is enabled */}
                        {aiEnabled && travelMateAdvice && (
                            <TravelMateAgent
                                advice={travelMateAdvice}
                                currency={searchQuery.currency}
                                results={hotelResults}
                                searchQuery={searchQuery}
                            />
                        )}

                        <div className="bg-white p-4 rounded-md shadow-sm mb-4">
                            <div className="flex justify-between items-center">
                                <p className="text-sm font-semibold">
                                    {t('showing_results')} {Math.min(displayLimit, filteredResults.length)} {t('of_results')} {filteredResults.length} {t('matches_your_search_criteria')}
                                </p>
                                {isLoadingAdvice && (
                                    <span className="text-xs text-indigo-500 font-bold animate-pulse flex items-center gap-2">
                                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        {t('ai_analyzing')}
                                    </span>
                                )}
                            </div>
                        </div>

                        {isLoading ? (
                            <LoadingProgress commentary={searchCommentary} query={searchQuery} step={loadingStep} />
                        ) : mixedResults.length > 0 ? (
                            <div className="space-y-4">
                                {mixedResults}

                                {displayLimit < filteredResults.length && (
                                    <div ref={loadingMoreRef} className="py-8 text-center animate-pulse">
                                        <div className="inline-flex items-center text-amber-600 font-semibold">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('loading_more')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-white p-10 rounded-lg shadow-md text-center">
                                <p className="text-lg text-gray-600">{t('no_results')}</p>
                                <button onClick={() => navigate('/')} className="mt-4 text-gray-500 text-sm">
                                    {t('try_adjusting')}
                                </button>
                            </div>
                        )}
                    </main>
                </div>
            </div>

            {/* Integrated Travel Mate Assistant Drawer */}
            {searchQuery && (
                <TravelAssistant
                    isOpen={isAssistantOpen}
                    onClose={() => setIsAssistantOpen(false)}
                    results={hotelResults}
                    searchQuery={searchQuery}
                />
            )}

            {/* Floating Action Button to Trigger Assistant - BLACK BUTTON */}
            <button
                onClick={() => setIsAssistantOpen(true)}
                className="fixed bottom-6 right-6 z-40 bg-gray-900 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center hover:bg-amber-500 hover:scale-110 transition-all duration-300 group"
            >
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse border-2 border-white"></div>
                <span className="text-2xl group-hover:rotate-12 transition-transform">✨</span>
            </button>

        </div>
    );
};
